import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import sessionsApi from 'api/requests/sessions';
import Button from 'components/_UI/Button';
import Checkbox from 'components/_UI/Checkbox';
import Fieldset from 'components/_UI/Fieldset';
import Loader from 'components/_UI/Loader';
import Modal from 'components/_UI/Modal';
import Text from 'components/_UI/Text';
import TextArea from 'components/_UI/TextArea';
import { useEvent } from 'context/events/Event';
import { SESSION_KINDS, ticketProducts } from 'helpers/eventSessions';
import { Column } from 'styled/alignment/Column';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import fonts from 'theme/fonts';

function firstError(error) {
    const errors = error?.response?.data?.errors;

    if (errors) {
        return Object.values(errors).flat()[0];
    }

    return error?.response?.data?.message || 'Não foi possível salvar a sessão.';
}

export default function SessionForm({ edit = null, onClose, onSuccess, defaultDate }) {
    const { event } = useEvent();
    const tickets = ticketProducts(event);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [productIds, setProductIds] = useState(edit?.product_ids || []);
    const [openAccess, setOpenAccess] = useState(!!edit?.open_access);

    const {
        register,
        handleSubmit,
        control,
        formState: { errors },
    } = useForm({
        defaultValues: {
            title: edit?.title || '',
            kind: edit?.kind || 'talk',
            date: edit?.date || defaultDate || '',
            start: edit?.start || '',
            end: edit?.end || '',
            location: edit?.location || '',
            speaker: edit?.speaker || '',
            description: edit?.description || '',
            capacity: edit?.capacity || '',
        },
    });

    function toggleTicket(id) {
        setProductIds(current => (current.includes(id) ? current.filter(item => item !== id) : [...current, id]));
    }

    function createPayload(content) {
        return {
            title: content.title,
            kind: content.kind,
            date: content.date,
            start: content.start,
            end: content.end || null,
            location: content.location || null,
            speaker: content.speaker || null,
            description: content.description || null,
            capacity: content.capacity ? Number(content.capacity) : null,
            open_access: openAccess,
            product_ids: productIds,
        };
    }

    async function submit(content) {
        setLoading(true);
        setError('');

        try {
            const payload = createPayload(content);

            if (edit) {
                await sessionsApi.update(event.slug, edit.id, payload);
            } else {
                await sessionsApi.create(event.slug, payload);
            }

            onSuccess(edit ? 'editSession' : 'createSession');
        } catch (err) {
            setError(firstError(err));
        } finally {
            setLoading(false);
        }
    }

    return (
        <Modal isOpen onClose={onClose}>
            <Text name='highlight'>{edit ? 'Editar' : 'Adicionar'} sessão</Text>

            <Form onSubmit={handleSubmit(submit)}>
                <Fieldset
                    label='Título *'
                    placeholder='Ex.: IA no mercado de trabalho'
                    register={register}
                    name='title'
                    layout='squired'
                    validations={{ required: 'Informe o título da sessão', maxLength: { value: 255, message: 'Use até 255 caracteres' } }}
                    error={errors?.title?.message}
                />

                <Field>
                    <Label htmlFor='session-kind'>Tipo</Label>
                    <Select id='session-kind' {...register('kind')}>
                        {SESSION_KINDS.map(kind => (
                            <option key={kind.value} value={kind.value}>{kind.label}</option>
                        ))}
                    </Select>
                </Field>

                <Row>
                    <Fieldset
                        label='Dia *'
                        inputType='date'
                        register={register}
                        name='date'
                        layout='squired'
                        validations={{ required: 'Informe o dia' }}
                        error={errors?.date?.message}
                    />
                    <Fieldset
                        label='Início *'
                        inputType='time'
                        register={register}
                        name='start'
                        layout='squired'
                        validations={{ required: 'Informe o horário' }}
                        error={errors?.start?.message}
                    />
                    <Fieldset
                        label='Fim'
                        inputType='time'
                        register={register}
                        name='end'
                        layout='squired'
                    />
                </Row>

                <Row>
                    <Fieldset
                        label='Local (sala, auditório)'
                        placeholder='Ex.: Auditório Principal'
                        register={register}
                        name='location'
                        layout='squired'
                    />
                    <Fieldset
                        label='Palestrante'
                        placeholder='Ex.: Profa. Helena Duarte'
                        register={register}
                        name='speaker'
                        layout='squired'
                    />
                </Row>

                <Fieldset
                    label='Vagas (opcional)'
                    placeholder='Ex.: 30'
                    inputType='number'
                    register={register}
                    name='capacity'
                    layout='squired'
                    validations={{ min: { value: 1, message: 'Use 1 ou mais' } }}
                    error={errors?.capacity?.message}
                />

                <Field>
                    <Label>Descrição (opcional)</Label>
                    <Controller
                        name='description'
                        control={control}
                        render={({ field }) => (
                            <TextArea
                                {...field}
                                placeholder='Conte o que será abordado'
                                register={register}
                                name='description'
                                squired
                            />
                        )}
                    />
                </Field>

                <Group>
                    <Label>Quem entra nesta sessão</Label>
                    <Hint>Marque os ingressos que dão acesso. O ingresso continua sendo comprado normalmente.</Hint>

                    <Checkbox
                        label='Qualquer ingresso do evento'
                        selected={openAccess}
                        onSelect={() => setOpenAccess(current => !current)}
                        marginTop='8px'
                    />

                    {!openAccess && (
                        tickets.length ? tickets.map(ticket => (
                            <Checkbox
                                key={ticket.id}
                                label={ticket.name}
                                selected={productIds.includes(ticket.id)}
                                onSelect={() => toggleTicket(ticket.id)}
                                marginTop='0px'
                            />
                        )) : (
                            <Hint>Este evento ainda não tem ingressos. Cadastre-os para ligar a esta sessão.</Hint>
                        )
                    )}
                </Group>

                {error && <ErrorText role='alert'>{error}</ErrorText>}

                {loading ? (
                    <Loader />
                ) : (
                    <Actions>
                        <Button variant='light' category='roundedBorded' onClick={onClose}>
                            Cancelar
                        </Button>
                        <Button variant='featured' type='submit'>
                            {edit ? 'Atualizar' : 'Adicionar'}
                        </Button>
                    </Actions>
                )}
            </Form>
        </Modal>
    );
}

const Form = styled.form`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const Row = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 6px;

    @media screen and (min-width: ${breakpoints.md}) {
        grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
        gap: 12px;
    }
`;

const Field = styled(Column)`
    width: 100%;
    align-items: flex-start;
    margin-top: 10px;
`;

const Label = styled.label`
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    color: ${colors.nightDark};
    margin-bottom: 5px;
`;

const Select = styled.select`
    width: 100%;
    padding: 11px 12px;
    border-radius: 8px;
    border: 1px solid ${colors.nightLight};
    background: ${colors.neutralLight};
    font-family: inherit;
    font-size: ${fonts.sizes.default};
    color: ${colors.nightDark};
`;

const Group = styled(Column)`
    width: 100%;
    align-items: flex-start;
    margin-top: 14px;
    padding: 12px 14px;
    border-radius: 10px;
    background: ${colors.neutral};
    max-height: 220px;
    overflow-y: auto;
`;

const Hint = styled.span`
    font-size: ${fonts.sizes.small};
    color: ${colors.night};
    line-height: 1.4;
    text-align: left;
`;

const ErrorText = styled.p`
    margin: 8px 0 0;
    font-size: ${fonts.sizes.small};
    color: ${colors.danger};
`;

const Actions = styled(Column)`
    margin-top: 15px;

    @media screen and (min-width: ${breakpoints.md}) {
        flex-direction: row;
        gap: 5px;
    }
`;
